import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classnames from 'classnames'
import { Field, useFormikContext } from 'formik'
import * as React from 'react'
import { Link, useOutletContext, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess, useApi, useDebouncedLoadOptions } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import * as Table from 'src/tailwind/components/Table'
import AsyncSelect from 'src/views/components/AsyncSelect'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput, { type Option as SelectInputOption } from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import Select from 'src/views/components/Select'
import SemesterSelect from 'src/views/includes/SemesterSelect'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import Swal from 'sweetalert2'
import type { AdministratorStudentChoicesOutletContext } from './AdministratorStudentChoicesLayout'
import { ChoiceCreditCells } from './ChoiceCreditCells'
import {
  CHOICE_INPUT_CLASS_NAME,
  choicePatchBodyOf,
  choiceRowValuesOf,
  CREDIT_TYPE_DOCTORAL,
  CREDIT_TYPE_PASS_FAIL,
  editableFieldsOf,
  hasRowChanged,
  useStudentChoiceParams,
  type ChoicesFormValues,
} from './constants'

export default function AdministratorStudentChoicesAddCoursePage(): JSX.Element {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t(['კურსის დამატება', 'Add course'])}>
      <PageContent />
    </SuspenseWrapper>
  )
}

interface SelectOption {
  readonly id: string
  readonly name: string
}

interface CourseOptionGroup {
  readonly label: string
  readonly options: readonly Api.AdministratorChoiceCourseOption[]
}

const ILIAUNI_CREDIT_TYPE = 1

function groupCourseOptions(
  courses: readonly Api.AdministratorChoiceCourseOption[],
  iliauniLabel: string,
  otherLabel: string
): readonly CourseOptionGroup[] {
  const iliauniCourses = courses.filter((c) => c.creditType === ILIAUNI_CREDIT_TYPE)
  const otherCourses = courses.filter((c) => c.creditType !== ILIAUNI_CREDIT_TYPE)
  const groups: CourseOptionGroup[] = []
  if (iliauniCourses.length > 0) groups.push({ label: iliauniLabel, options: iliauniCourses })
  if (otherCourses.length > 0) groups.push({ label: otherLabel, options: otherCourses })
  return groups
}

interface HistoryFormValues {
  readonly score: string
  readonly credits: string
  readonly latScore: string
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const canManageUsers = useAdminAccess().canManage('userStudents')
  const locale = useLocale()
  const headers = useAuthenticatedHeaders()
  const { uid, profileId } = useStudentChoiceParams()
  const activeSemester = useActiveSemester()
  const {
    coursesPage: layoutCoursesPage,
    targetSemesterId: layoutTargetSemesterId,
    mutateCoursesPage,
  } = useOutletContext<AdministratorStudentChoicesOutletContext>()

  const [facultyId, setFacultyId] = React.useState('')
  const [creditType, setCreditType] = React.useState('')
  const [generalOnly, setGeneralOnly] = React.useState(false)
  const [selectedCourse, setSelectedCourse] = React.useState<Api.AdministratorChoiceCourseOption | null>(null)

  const [editingUids, setEditingUids] = React.useState<ReadonlySet<string>>(() => new Set())

  const [searchParams, setSearchParams] = useSearchParams()
  const semId = searchParams.get('semId')

  React.useEffect(() => {
    const semFromUrl = searchParams.get('semId')
    if (semFromUrl != null && semFromUrl !== '') return
    if (activeSemester?.id == null) return
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.set('semId', String(activeSemester.id))
        return next
      },
      { replace: true }
    )
  }, [searchParams, activeSemester?.id, setSearchParams])

  const hasSetDefaultCreditType = React.useRef(false)

  React.useEffect(() => {
    setFacultyId('')
    setCreditType('')
    setGeneralOnly(false)
    setSelectedCourse(null)
    setEditingUids(new Set())
    hasSetDefaultCreditType.current = false
  }, [semId])

  const targetSemesterId = semId != null && semId !== '' ? parseInt(semId, 10) : null

  const {
    data: choicesPage,
    mutate: mutateChoices,
    isLoading: isChoicesLoading,
  } = useApi({
    endpoint: Api.getAdministratorStudentChoices,
    params: React.useMemo(
      () => ({
        headers,
        args: { uid, profileId },
        query: targetSemesterId != null ? { targetSemesterId } : {},
      }),
      [headers, uid, profileId, targetSemesterId]
    ),
    shouldFetch: uid !== '' && profileId !== '' && targetSemesterId != null,
  })

  const activeSemesterId = choicesPage?.activeSemester.id
  const isActiveSemesterTarget =
    targetSemesterId != null && activeSemesterId != null && targetSemesterId === activeSemesterId

  const { data: faculties } = useApi({
    endpoint: Api.getRegimeFaculties,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const { data: creditTypesResponse } = useApi({
    endpoint: Api.getAdministratorListsCreditTypes,
    params: React.useMemo(() => ({ headers }), [headers]),
  })
  const creditTypes = React.useMemo(() => creditTypesResponse?.data ?? [], [creditTypesResponse?.data])

  const { data: scoreOptionsResponse } = useApi({
    endpoint: Api.getAdministratorListsScoreOptions,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const doctoralScoreOptions: readonly SelectInputOption[] = React.useMemo(
    () => (scoreOptionsResponse?.data.doctoral ?? []).map((o) => ({ value: String(o.id), label: o.name })),
    [scoreOptionsResponse?.data.doctoral]
  )

  const passFailScoreOptions: readonly SelectInputOption[] = React.useMemo(
    () => (scoreOptionsResponse?.data.passFail ?? []).map((o) => ({ value: String(o.id), label: o.name })),
    [scoreOptionsResponse?.data.passFail]
  )

  const doctoralScoreNames = React.useMemo(
    () => new Map((scoreOptionsResponse?.data.doctoral ?? []).map((o) => [o.id, o.name])),
    [scoreOptionsResponse?.data.doctoral]
  )

  const passFailScoreNames = React.useMemo(
    () => new Map((scoreOptionsResponse?.data.passFail ?? []).map((o) => [o.id, o.name])),
    [scoreOptionsResponse?.data.passFail]
  )

  const formatChoiceScore = React.useCallback(
    (choice: Api.AdministratorChoiceRow): string | number => {
      if (choice.creditType === CREDIT_TYPE_DOCTORAL) return doctoralScoreNames.get(choice.score) ?? choice.score
      if (choice.creditType === CREDIT_TYPE_PASS_FAIL) return passFailScoreNames.get(choice.score) ?? choice.score
      return choice.score
    },
    [doctoralScoreNames, passFailScoreNames]
  )

  React.useEffect(() => {
    if (hasSetDefaultCreditType.current) return
    const firstCreditType = creditTypes[0]
    if (firstCreditType == null) return
    hasSetDefaultCreditType.current = true
    setCreditType(String(firstCreditType.id))
  }, [creditTypes, semId])

  const facultyOptions: readonly SelectOption[] = React.useMemo(
    () => [{ id: '', name: t('common:any') }, ...(faculties ?? []).map((f) => ({ id: String(f.id), name: f.name }))],
    [faculties, t]
  )

  const creditTypeOptions: readonly SelectOption[] = React.useMemo(
    () => [{ id: '', name: t('common:any') }, ...creditTypes.map((ct) => ({ id: String(ct.id), name: ct.name }))],
    [creditTypes, t]
  )

  const iliauniGroupLabel = t(['ილიაუნის კურსები', "IliaUni's courses"])
  const otherGroupLabel = t(['სხვა კურსები', 'Other courses'])

  const loadCourseOptions = useDebouncedLoadOptions(
    React.useCallback(
      async (q: string) => {
        if (targetSemesterId == null) return []
        const result = await Api.getAdministratorStudentChoiceCourses({
          headers,
          args: { uid, profileId },
          query: {
            targetSemesterId,
            ...(q.length > 0 ? { q } : {}),
            ...(facultyId !== '' ? { facultyId: parseInt(facultyId, 10) } : {}),
            ...(creditType !== '' ? { creditType: parseInt(creditType, 10) } : {}),
            general: generalOnly ? 1 : 0,
          },
        })
        return groupCourseOptions(result.courses, iliauniGroupLabel, otherGroupLabel)
      },
      [
        headers,
        uid,
        profileId,
        targetSemesterId,
        facultyId,
        creditType,
        generalOnly,
        iliauniGroupLabel,
        otherGroupLabel,
      ]
    )
  )

  /** Bumped after a course is added so the course select drops its cached options and loads them again. */
  const [courseOptionsVersion, setCourseOptionsVersion] = React.useState(0)

  const refreshCourseOptions = React.useCallback(async () => {
    await mutateCoursesPage()
    setCourseOptionsVersion((version) => version + 1)
  }, [mutateCoursesPage])

  const isDefaultCourseFilter = facultyId === '' && creditType === '' && !generalOnly
  const defaultCourseOptions: boolean | readonly CourseOptionGroup[] =
    isDefaultCourseFilter && layoutTargetSemesterId === targetSemesterId && layoutCoursesPage != null
      ? groupCourseOptions(layoutCoursesPage.courses, iliauniGroupLabel, otherGroupLabel)
      : true

  /**
   * The credit type the row is created with is the one picked in the "Credits type" select, not the
   * catalog course's own type — the catalog only ever serves IliaUni (type 1) courses, so taking it
   * from the course would store every row as an IliaUni credit and lose the recognized / pre-Argus
   * marking the administrator asked for.
   */
  const selectedCreditType = creditType !== '' ? parseInt(creditType, 10) : null

  const handleAddCurrent = React.useCallback(
    async (course: Api.AdministratorChoiceCourseOption) => {
      try {
        const result = await Api.postAdministratorStudentChoice({
          headers,
          args: { uid, profileId },
          body: {
            courseId: course.id,
            targetSemesterId: activeSemesterId ?? undefined,
            creditType: selectedCreditType ?? course.creditType,
          },
        })
        await Promise.all([mutateChoices(), refreshCourseOptions()])
        toast.success(t('common:updated_successfully'))
        // The course is added either way; a notice (e.g. an unmet prerequisite) is only a warning.
        for (const notice of result.notices ?? []) toast.warning(notice.message)
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
      }
    },
    [activeSemesterId, headers, mutateChoices, profileId, refreshCourseOptions, selectedCreditType, t, uid]
  )

  const isDoctoral = creditType === String(CREDIT_TYPE_DOCTORAL)
  const isPassFail = creditType === String(CREDIT_TYPE_PASS_FAIL)
  const isNumeric = !isDoctoral && !isPassFail

  /**
   * Only past semesters and doctoral / pass-fail credits take a hand-entered score and credit amount.
   * On the active semester recognized and pre-Argus credits are added like IliaUni ones, with neither field.
   */
  const isHistoryEntry = !isActiveSemesterTarget || isDoctoral || isPassFail

  const handleSubmitHistory = React.useCallback(
    async (values: HistoryFormValues) => {
      if (selectedCourse == null || targetSemesterId == null) return
      try {
        await Api.postAdministratorStudentChoiceHistory({
          headers,
          args: { uid, profileId },
          body: {
            courseId: selectedCourse.id,
            targetSemesterId: targetSemesterId,
            creditType: selectedCreditType ?? selectedCourse.creditType,
            // A blank field is left out rather than sent as NaN, which serialises to null.
            ...(isNumeric
              ? {
                  ...(values.score.trim() !== '' ? { score: parseInt(values.score, 10) } : {}),
                  ...(values.credits.trim() !== '' ? { credits: parseInt(values.credits, 10) } : {}),
                }
              : isDoctoral
                ? { latScore: values.latScore }
                : { latScore2: values.latScore }),
          },
        })
        toast.success(t('common:updated_successfully'))
        await Promise.all([mutateChoices(), refreshCourseOptions()])
        setSelectedCourse(null)
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
        throw error
      }
    },
    [
      selectedCourse,
      targetSemesterId,
      headers,
      uid,
      profileId,
      selectedCreditType,
      isNumeric,
      isDoctoral,
      mutateChoices,
      refreshCourseOptions,
      t,
    ]
  )

  const handleAddCurrentClick = React.useCallback(async () => {
    if (selectedCourse == null) return
    await handleAddCurrent(selectedCourse)
    setSelectedCourse(null)
  }, [selectedCourse, handleAddCurrent])

  const displayChoices = React.useMemo(
    () => choicesPage?.targetSemesterChoices ?? [],
    [choicesPage?.targetSemesterChoices]
  )

  const editableChoices = React.useMemo(
    () =>
      displayChoices.filter((choice) => choice.capabilities?.update === true && editableFieldsOf(choice).length > 0),
    [displayChoices]
  )

  const toggleEditing = React.useCallback((choiceUid: string) => {
    setEditingUids((current) => {
      const next = new Set(current)
      if (!next.delete(choiceUid)) next.add(choiceUid)
      return next
    })
  }, [])

  const toggleEditingAll = React.useCallback(() => {
    setEditingUids((current) =>
      current.size === editableChoices.length ? new Set() : new Set(editableChoices.map((choice) => choice.uid))
    )
  }, [editableChoices])

  const stopEditing = React.useCallback(() => setEditingUids(new Set()), [])

  /**
   * Every row keeps its own entry so any number of them can be open at once — the legacy screen
   * enables the inputs row by row and saves all of the enabled rows with a single Update.
   */
  const editInitialValues = React.useMemo<ChoicesFormValues>(
    () => ({
      rows: Object.fromEntries(displayChoices.map((choice) => [choice.uid, choiceRowValuesOf(choice)])),
    }),
    [displayChoices]
  )

  const handleUpdate = React.useCallback(
    async (values: ChoicesFormValues) => {
      if (targetSemesterId == null || editingUids.size === 0) return

      // There is no bulk endpoint, so the open rows are patched one by one, in the order shown.
      const failures: { readonly uid: string; readonly message: string }[] = []
      let updatedCount = 0

      for (const choice of displayChoices) {
        if (!editingUids.has(choice.uid)) continue
        const row = values.rows[choice.uid]
        if (row == null) continue
        const allowed = editableFieldsOf(choice)
        if (!hasRowChanged(choice, row, allowed)) continue
        try {
          await Api.patchAdministratorStudentChoice({
            headers,
            args: { uid, profileId, choiceUid: choice.uid },
            body: {
              targetSemesterId,
              ...choicePatchBodyOf(choice, row, allowed),
            },
          })
          updatedCount++
        } catch (error) {
          failures.push({ uid: choice.uid, message: errorMessage(error as base.CommonError) })
        }
      }

      // Rejected rows stay open so they can be corrected; the saved ones close.
      setEditingUids(new Set(failures.map((failure) => failure.uid)))
      await mutateChoices()

      if (failures.length > 0) {
        toast.error(
          failures
            .map(
              (failure) =>
                `${displayChoices.find((choice) => choice.uid === failure.uid)?.courseName ?? ''}: ${failure.message}`
            )
            .join('\n')
        )
        return
      }
      if (updatedCount > 0) toast.success(t('common:updated_successfully'))
    },
    [targetSemesterId, editingUids, displayChoices, headers, mutateChoices, profileId, t, uid]
  )

  const handleDelete = React.useCallback(
    async (choiceUid: string) => {
      if (targetSemesterId == null) return
      const alert = await Swal.fire({
        title: t('common:are_you_sure'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('common:confirm'),
        cancelButtonText: t('common:cancel'),
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6C757D',
      })
      if (!alert.isConfirmed) return
      try {
        await Api.deleteAdministratorStudentChoice({
          headers,
          args: { uid, profileId, choiceUid },
          query: { targetSemesterId: targetSemesterId },
        })
        await mutateChoices()
        toast.success(t(['წარმატებით წაიშალა', 'Successfully deleted']))
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
      }
    },
    [targetSemesterId, headers, mutateChoices, profileId, t, uid]
  )

  return (
    <div className="mt-4 w-full space-y-6">
      <SemesterSelect />
      <fieldset className="min-w-0 rounded-card bg-card px-6 py-8">
        {isChoicesLoading ? (
          <Loader className="m-auto flex py-8" />
        ) : displayChoices.length === 0 ? (
          <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
        ) : (
          <Form initialValues={editInitialValues} enableReinitialize onSubmit={handleUpdate}>
            <ChoicesEditor
              choices={displayChoices}
              editableChoices={editableChoices}
              editingUids={editingUids}
              formatScore={formatChoiceScore}
              onToggleEditing={toggleEditing}
              onToggleEditingAll={toggleEditingAll}
              onStopEditing={stopEditing}
              onDelete={handleDelete}
            />
          </Form>
        )}
      </fieldset>

      {canManageUsers && (
        <fieldset className="min-w-0 rounded-card bg-card px-6 py-8">
          <h3 className="mb-4 font-semibold text-primaryTextColor">{t(['კურსის დამატება', 'Add course'])}</h3>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-[10px] block font-bold text-primaryTextColor">{t('faculty:major_faculty')}</label>
              <Select<SelectOption>
                options={facultyOptions}
                value={facultyOptions.find((f) => f.id === facultyId) ?? facultyOptions[0]}
                getOptionValue={(f) => f.id}
                getOptionLabel={(f) => f.name}
                onChange={(option) => {
                  setFacultyId(option?.id ?? '')
                  setSelectedCourse(null)
                }}
                menuPortalTarget={document.body}
              />
            </div>
            <div>
              <label className="mb-[10px] block font-bold text-primaryTextColor">
                {t(['კრედიტის ტიპი', 'Credits type'])}
              </label>
              <Select<SelectOption>
                options={creditTypeOptions}
                value={creditTypeOptions.find((c) => c.id === creditType) ?? creditTypeOptions[0]}
                getOptionValue={(c) => c.id}
                getOptionLabel={(c) => c.name}
                onChange={(option) => {
                  setCreditType(option?.id ?? '')
                  setSelectedCourse(null)
                }}
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-primaryTextColor">
            <input
              type="checkbox"
              checked={generalOnly}
              onChange={(e) => {
                setGeneralOnly(e.target.checked)
                setSelectedCourse(null)
              }}
              className="size-4 rounded border-borderGrey"
            />
            {t('course:general_module')}
          </label>

          <div className="mt-4">
            <label className="mb-[10px] block font-bold text-primaryTextColor">{t('course:course_name')}</label>
            <AsyncSelect<Api.AdministratorChoiceCourseOption, false, CourseOptionGroup>
              key={`${facultyId}-${creditType}-${String(generalOnly)}-${String(targetSemesterId)}-${courseOptionsVersion}`}
              value={selectedCourse}
              loadOptions={loadCourseOptions}
              defaultOptions={defaultCourseOptions}
              cacheOptions
              getOptionValue={(c) => String(c.id)}
              getOptionLabel={(c) => `${c.courseCode} - ${locale === 'en' ? c.courseNameEn : c.courseName}`}
              onChange={(option) => setSelectedCourse(option)}
              isDisabled={targetSemesterId == null}
              isClearable
              menuPortalTarget={document.body}
            />
          </div>

          {isHistoryEntry && (
            <Form<HistoryFormValues>
              key={selectedCourse?.id ?? 'none'}
              initialValues={{
                score: '',
                credits: selectedCourse != null ? String(selectedCourse.credits) : '',
                latScore: '',
              }}
              onSubmit={handleSubmitHistory}
            >
              <FormError />
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <TextInput type="number" name="credits" label={t('common:credits')} />
                {isNumeric && <TextInput type="number" name="score" label={t('common:score')} />}
                {isDoctoral && (
                  <SelectInput name="latScore" label={t(['შეფასება', 'Evaluation'])} options={doctoralScoreOptions} />
                )}
                {isPassFail && (
                  <SelectInput name="latScore" label={t(['შეფასება', 'Evaluation'])} options={passFailScoreOptions} />
                )}
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="blue" type="submit" disabled={selectedCourse == null}>
                  {t('common:add')}
                </Button>
              </div>
            </Form>
          )}

          {!isHistoryEntry && (
            <div className="mt-4 flex justify-end">
              <Button
                variant="blue"
                type="button"
                disabled={selectedCourse == null}
                onClick={() => void handleAddCurrentClick()}
              >
                {t('common:add')}
              </Button>
            </div>
          )}
        </fieldset>
      )}
    </div>
  )
}

interface ChoicesEditorProps {
  readonly choices: readonly Api.AdministratorChoiceRow[]
  readonly editableChoices: readonly Api.AdministratorChoiceRow[]
  readonly editingUids: ReadonlySet<string>
  readonly formatScore: (choice: Api.AdministratorChoiceRow) => string | number
  readonly onToggleEditing: (choiceUid: string) => void
  readonly onToggleEditingAll: () => void
  readonly onStopEditing: () => void
  readonly onDelete: (choiceUid: string) => Promise<void>
}

function ChoicesEditor({
  choices,
  editableChoices,
  editingUids,
  formatScore,
  onToggleEditing,
  onToggleEditingAll,
  onStopEditing,
  onDelete,
}: ChoicesEditorProps): JSX.Element {
  const t = useTranslatable()
  const formik = useFormikContext<ChoicesFormValues>()

  const isEditingAll = editableChoices.length > 0 && editingUids.size === editableChoices.length

  const handleToggleEditing = React.useCallback(
    (choiceUid: string) => {
      // Closing a row drops what was typed into it, the way cancelling the row editor did.
      if (editingUids.has(choiceUid)) {
        const initialRow = formik.initialValues.rows[choiceUid]
        if (initialRow != null) void formik.setFieldValue(`rows.${choiceUid}`, initialRow)
      }
      onToggleEditing(choiceUid)
    },
    [editingUids, formik, onToggleEditing]
  )

  const handleCancel = React.useCallback(() => {
    formik.resetForm()
    onStopEditing()
  }, [formik, onStopEditing])

  return (
    <>
      <Table.Table className="w-full table-auto">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('course:course_name')}</Table.Th>
            <Table.Th>{t(['კოდი', 'Code'])}</Table.Th>
            <Table.Th>{t(['ზოგადი მოდული', 'General Module'])}</Table.Th>
            <Table.Th>{t(['კურ. კრედ.', 'Course credits'])}</Table.Th>
            <Table.Th>{t(['მინ. კრედიტი', 'Min. credit'])}</Table.Th>
            <Table.Th>{t(['ქულა', 'Score'])}</Table.Th>
            <Table.Th>{t(['ჯგუფი', 'Group'])}</Table.Th>
            <Table.Th>{t(['მასწავლებელი', 'Teacher(s)'])}</Table.Th>
            <Table.Th>
              {editableChoices.length > 0 && (
                <button
                  type="button"
                  className="whitespace-nowrap text-caption text-primaryBlueLink hover:underline"
                  onClick={onToggleEditingAll}
                >
                  {isEditingAll ? t('choice:stop_editing_all') : t('choice:edit_all')}
                </button>
              )}
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {choices.map((choice) => (
            <ChoiceRow
              key={choice.uid}
              choice={choice}
              isEditing={editingUids.has(choice.uid)}
              formatScore={formatScore}
              onToggleEditing={handleToggleEditing}
              onDelete={onDelete}
            />
          ))}
        </Table.Tbody>
      </Table.Table>
      {editingUids.size > 0 && (
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={handleCancel}>
            {t('common:cancel')}
          </Button>
          <Button variant="blue" type="submit">
            {t('common:update')}
          </Button>
        </div>
      )}
    </>
  )
}

interface ChoiceRowProps {
  readonly choice: Api.AdministratorChoiceRow
  readonly isEditing: boolean
  readonly formatScore: (choice: Api.AdministratorChoiceRow) => string | number
  readonly onToggleEditing: (choiceUid: string) => void
  readonly onDelete: (choiceUid: string) => Promise<void>
}

function ChoiceRow({ choice, isEditing, formatScore, onToggleEditing, onDelete }: ChoiceRowProps): JSX.Element {
  const t = useTranslatable()
  const canManageUsers = useAdminAccess().canManage('userStudents')
  const locale = useLocale()

  const editableFields = React.useMemo(() => editableFieldsOf(choice), [choice])
  const canEdit = canManageUsers && choice.capabilities?.update === true && editableFields.length > 0

  const handleToggleEditing = React.useCallback(() => onToggleEditing(choice.uid), [choice.uid, onToggleEditing])
  const handleDelete = React.useCallback(() => void onDelete(choice.uid), [choice.uid, onDelete])

  const courseName = locale === 'en' ? (choice.courseNameEn ?? choice.courseName) : choice.courseName

  return (
    <Table.Tr
      className={classnames({
        'bg-lightRed': choice.rowClass === 'error',
        'bg-lightGreen': choice.rowClass === 'success',
        'bg-lightBlue': choice.rowClass === 'info',
      })}
    >
      <Table.Td className="rounded-l-lg">
        {isEditing && editableFields.includes('couName') ? (
          <Field type="text" name={`rows.${choice.uid}.couName`} className={CHOICE_INPUT_CLASS_NAME} />
        ) : (
          courseName
        )}
      </Table.Td>
      <Table.Td>
        {isEditing && editableFields.includes('couCode') ? (
          <Field type="text" name={`rows.${choice.uid}.couCode`} className={CHOICE_INPUT_CLASS_NAME} />
        ) : (
          choice.courseCode
        )}
      </Table.Td>
      <Table.Td>
        {isEditing && editableFields.includes('couGeneral') ? (
          <Field
            type="checkbox"
            name={`rows.${choice.uid}.couGeneral`}
            className="size-4 rounded border-borderGrey"
            aria-label={t(['ზოგადი მოდული', 'General Module'])}
          />
        ) : choice.couGeneral === 1 ? (
          t(['დიახ', 'Yes'])
        ) : (
          t(['არა', 'No'])
        )}
      </Table.Td>
      <ChoiceCreditCells
        choice={choice}
        isEditing={isEditing}
        editableFields={editableFields}
        formattedScore={formatScore(choice)}
      />
      <Table.Td>{choice.group?.name ?? '-'}</Table.Td>
      <Table.Td>
        {choice.teachers != null && choice.teachers.length > 0
          ? choice.teachers.map((teacher) => teacher.fullName).join(', ')
          : '-'}
      </Table.Td>
      <Table.Td className="rounded-r-lg">
        <div className="flex items-center">
          {canEdit && (
            <button
              type="button"
              className={classnames('flex size-7 items-center justify-center rounded-full p-1', {
                'bg-table-icon-hover': isEditing,
                'hover:bg-table-icon-hover': !isEditing,
              })}
              onClick={handleToggleEditing}
              aria-label={t('common:edit')}
              aria-pressed={isEditing}
            >
              <FontAwesomeIcon
                icon={ProRegularSvgIcons.faPencil}
                color={isEditing ? undefined : 'gray'}
                className={classnames({ 'text-primaryBlueLink': isEditing })}
                size="lg"
              />
            </button>
          )}
          {canManageUsers && choice.capabilities?.delete === true && (
            <button
              type="button"
              className="flex size-7 items-center justify-center rounded-full p-1 hover:bg-table-icon-hover"
              onClick={handleDelete}
              aria-label={t('common:delete')}
            >
              <FontAwesomeIcon icon={ProRegularSvgIcons.faTrashCan} color="gray" size="lg" />
            </button>
          )}
          {choice.courseId != null && (
            <Link
              to={`/${locale}/administrator/administration/courses/${choice.courseId}/students?semId=${choice.semId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex size-7 items-center justify-center rounded-full p-1 hover:bg-table-icon-hover"
              aria-label={t(['კურსზე გადასვლა', 'Go to course'])}
            >
              <FontAwesomeIcon icon={ProRegularSvgIcons.faArrowUpRightFromSquare} color="gray" size="lg" />
            </Link>
          )}
        </div>
      </Table.Td>
    </Table.Tr>
  )
}
